import {useEffect, useState} from "react";

const formatHeader = (key) => {
  if (!key) return "";
  return key
    .replace(/([A-Z])/g, " $1") // Insert space before capital letters
    .replace(/_/g, " ") // Replace underscores with space
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatValue = (key, value, row, openModal, openEditModal) => {
  if (key === "preview") {
    return (
      <div
        className="min-h-[100px] cursor-pointer"
        onClick={() => openModal(value.value, key)}
      >
        {(value?.value?.toLowerCase().includes(".jpg") ||
          value?.value?.toLowerCase().includes(".jpeg") ||
          value?.value?.toLowerCase().includes(".png")) && (
          <img src={`${value.value}`} />
        )}
        {value?.value?.toLowerCase().includes(".pdf") && (
          <object
            data={`${value.value}`}
            type="application/pdf"
            width="100%"
            height="100%"
          ></object>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="text-base">
        {key?.trim() == "Valid Document?" && value?.value === "No" && (
          <h5 style={{color: "#fd4f98"}}>{value?.value}</h5>
        )}
        {key?.trim() == "Valid Document?" && value?.value === "Yes" && (
          <h5>{value?.value}</h5>
        )}
        {key?.trim() !== "Valid Document?" && <h5>{value?.value}</h5>}
      </div>

      <div className="text-gray-400 text-sm">
        {value?.confidence != -1 &&
          value?.confidence &&
          key?.trim() !== "filename" &&
          key?.trim() !== "Valid Document?" && (
            <h6>Confidence: {value?.confidence}</h6>
          )}
        {(value?.confidence == -1 || !value?.value) && (
          <h6
            style={{
              color: "#fd4f98",
              opacity: "1",
              cursor: "pointer",
            }}
            onClick={() => openEditModal(row)}
          >
            NO DATA
          </h6>
        )}
        {value?.value && parseInt(value?.confidence) < 85 && (
          <h6
            style={{
              color: "#fd4f98",
              opacity: "1",
              cursor: "pointer",
            }}
            onClick={() => openEditModal(row)}
          >
            HUMAN REVIEW
          </h6>
        )}
      </div>
    </div>
  );
};

const ImageModal = ({src, alt, onClose}) => {
  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 transition-opacity duration-300 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full relative flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        <div className="p-4 flex-shrink-0 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Document</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-red-600 transition rounded-full hover:bg-red-50"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable Image Container: Takes available vertical space and enables scrolling */}
        <div className="flex justify-center items-center p-4 flex-grow overflow-y-auto">
          <img
            src={src}
            alt={alt}
            // Removed max-h from img here, relying on the container's overflow for scrolling
            className="max-w-full h-auto object-contain rounded-lg shadow-lg"
            // Fallback for image loading
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://placehold.co/400x400/CCCCCC/000000?text=Image+Unavailable";
              e.target.className = "w-full h-auto";
            }}
          />
        </div>
      </div>
    </div>
  );
};

const EditModal = ({rowData, onClose, onSave, columnKeys}) => {
  const [formData, setFormData] = useState(rowData);

  const handleChange = (e) => {
    const {name, value} = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: {confidence: 100, value: value},
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const editableKeys = columnKeys.filter(
    (key) => key !== "preview" && key !== "file_key"
  );

  if (!rowData) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full relative flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 flex-shrink-0 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Edit Record</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-red-600 transition rounded-full hover:bg-red-50"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 flex-grow overflow-y-auto space-y-4">
          {editableKeys.map((key) => (
            <div key={key}>
              <label
                htmlFor={key}
                className="block text-sm font-medium text-gray-700"
              >
                {formatHeader(key)}
              </label>
              <input
                type={"text"}
                name={key}
                id={key}
                value={formData[key].value}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition shadow-md"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const Analysis = (props) => {
  const [analysis, setAnalysis] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // State for the Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");

  const openModal = (src, alt) => {
    setModalImageSrc(src);
    setModalImageAlt(alt);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImageSrc("");
    setModalImageAlt("");
  };

  const openEditModal = (row) => {
    setEditingRow({...row});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingRow(null);
  };

  const fetchAnalysis = (shouldRefresh) => {
    if (props.csvFiles.length > 0) {
      let filteredCsvFiles = props.csvFiles.filter(
        (file) => file.filetype === props.selectedType
      );

      let filteredFiles = [];
      props.files.forEach((e) => {
        const userExists = filteredCsvFiles.some(
          (user) => user.filename.split(".")[0] === e.filename.split(".")[0]
        );
        if (userExists === true) {
          filteredFiles.push(e);
        }
      });

      if (shouldRefresh === true) {
        setLoading(true);
      }
      const options = {
        method: "POST",
        body: JSON.stringify({
          files: filteredCsvFiles,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      };
      fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/get-analysis`, options)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 200) {
            let promises = [];
            data.result.forEach((element) => {
              let promise = new Promise(async (resolve, reject) => {
                console.log(`Element =======> ${JSON.stringify(element)}`);

                let formattedData = {
                  filename: {value: element.file},
                  file_key: {value: element.key},
                };
                const file = filteredFiles.find(
                  (e) => e.filename.split(".")[0] === element.file.split(".")[0]
                );

                const options = {
                  method: "POST",
                  body: JSON.stringify({
                    Key: file.key,
                  }),
                  headers: {
                    "Content-Type": "application/json",
                  },
                };
                let resp = await fetch(
                  `${process.env.REACT_APP_AWS_ENDPOINT}/get-signedurl-document`,
                  options
                );
                let data = await resp.json();
                if (data.statusCode === 200) {
                  formattedData = {
                    ...formattedData,
                    preview: {value: data.result},
                  };
                }

                element.data.forEach((e) => {
                  formattedData = {
                    ...formattedData,
                    [e.key]: {value: e.value, confidence: e.confidence},
                  };
                });

                resolve(formattedData);
              });

              promises.push(promise);
            });

            Promise.all(promises).then((data) => {
              console.log(
                `Formatted Analysis =======> ${JSON.stringify(data)}`
              );
              setAnalysis(data);
              setLoading(false);
            });
          }
        })
        .catch((error) => {
          console.log(`Error in getting signed document =======> ${error}`);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchAnalysis(true);
  }, [props.selectedType]);

  const handleSaveEdit = (updatedObj) => {
    console.log(`Update Obj ======> ${JSON.stringify(updatedObj)}`);
    let data = [];
    for (const [key, value] of Object.entries(updatedObj)) {
      if (key !== "filename" && key !== "file_key") {
        data.push({
          key: key,
          value: value.value,
          confidence: value.confidence,
        });
      }
    }

    const options = {
      method: "POST",
      body: JSON.stringify({
        filename: updatedObj.filename.value,
        key: updatedObj.file_key.value,
        data,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    };
    fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/update-file`, options)
      .then((resp) => resp.json())
      .then((data) => {
        fetchAnalysis(false);
        closeEditModal();
      })
      .catch((error) => {
        console.log(`Error in updating s3 file =======> ${error}`);
        fetchAnalysis(false);
        closeEditModal();
      });
  };

  if (loading === true) {
    return (
      <div className="w-full bg-gray-50 p-4 flex items-center justify-center font-sans">
        <svg
          className="animate-spin h-10 w-10 text-blue-600 mb-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="#011a3c"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (analysis.length === 0) {
    return (
      <div className="w-full p-8 text-center text-gray-500 flex items-center justify-center">
        <p className="text-xl font-medium text-gray-400">
          No data available to display in the table.
        </p>
      </div>
    );
  }

  const columnKeys = Object.keys(analysis[0]);

  return (
    <div className="min-h-screen w-full bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="overflow-x-auto shadow-xl rounded-xl">
          <table className="min-w-full divide-y divide-blue-200 bg-white">
            <thead className="bg-[#275ca7]">
              <tr>
                {columnKeys.map((key) => (
                  <th
                    key={key}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider overflow-hidden truncate"
                  >
                    {formatHeader(key)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {analysis.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className={`transition duration-150 ease-in-out ${
                    rowIndex % 2 === 0
                      ? "bg-white hover:bg-gray-100"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  {columnKeys.map((key) => (
                    <td
                      key={`${row.id || rowIndex}-${key}`}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-800"
                    >
                      {formatValue(
                        key,
                        row[key],
                        row,
                        openModal,
                        openEditModal
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <ImageModal
          src={modalImageSrc}
          alt={modalImageAlt}
          onClose={closeModal}
        />
      )}

      {isEditModalOpen && editingRow && (
        <EditModal
          rowData={editingRow}
          onClose={closeEditModal}
          onSave={handleSaveEdit}
          columnKeys={columnKeys}
        />
      )}

      {/* <Modal
        show={review !== undefined}
        className={`${styles.analysis__Modal__Container}`}
        onCancel={() => {
          setReview();
          setEdit(false);
        }}
        header={
          <div className={`${styles.analysis__Modal__Header}`}>
            <h2>{review?.filename?.value}</h2>
            <img
              src="/images/close-icon.svg"
              onClick={() => {
                setReview();
                setEdit(false);
              }}
            />
          </div>
        }
        footer={
          <div className={`${styles.analysis__Modal__Footer}`}>
            <div
              className={`${styles.analysis__Modal__Footer__ContentContainer}`}
            >
              {review &&
                Object.keys(review).map((key) => {
                  return (
                    <>
                      {key !== "filename" && key !== "file_key" && (
                        <div
                          className={`${styles.analysis__Modal__Footer__Segment}`}
                        >
                          <div
                            className={`${styles.analysis__Modal__Footer__Header}`}
                          >
                            <h6>{key}</h6>
                          </div>
                          <div
                            className={`${styles.analysis__Modal__Footer__Value}`}
                          >
                            {edit === false && <h5>{review[key].value}</h5>}
                            {edit === true && (
                              <input
                                id={key}
                                placeholder={review[key].value}
                                onChange={changeHandler}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })}
            </div>

            <div
              className={`${styles.analysis__Modal__Footer__ButtonContainer}`}
            >
              <div className={`${styles.analysis__Modal__Footer__Button}`}>
                {edit === false && (
                  <Button size="standard" onClick={() => setEdit(true)}>
                    EDIT
                  </Button>
                )}
                {edit === true && (
                  <Button size="standard" onClick={() => handleSaveEdit()}>
                    SUBMIT
                  </Button>
                )}
              </div>
              <div className={`${styles.analysis__Modal__Footer__Button}`}>
                {edit === true && (
                  <Button
                    size="standard"
                    inverse
                    onClick={() => {
                      setEdit(false);
                    }}
                  >
                    CANCEL
                  </Button>
                )}
              </div>
            </div>
          </div>
        }
      ></Modal> */}
    </div>
  );
};

export default Analysis;
